import { Button, Col, DatePicker, Form, Input, message, Row, Select } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import * as React from 'react';
import { IMallPharmacyDetail } from 'src/entity/MallEntity';
import { IDoctorSearch } from 'src/entity/SearchEntity';
import { CertificationLevel } from 'src/services/DoctorService';
import I18N from 'src/services/I18NService';
import LocationService from 'src/services/LocationService';
import { IAreaInfo } from 'src/entity/LocationEntity';
import moment from 'moment';
import SupplierPharmacySelect from 'src/components/common/SupplierPharmacySelect';
import { ISales } from 'src/entity/DoctorEntity';
import SalesSearchBox from 'src/components/common/SalesSearchBox';

const { RangePicker } = DatePicker;

interface IDoctorSearchFormProps extends FormComponentProps {
  isDomestic: boolean;
  isNeedSetting: boolean; // 是否是待配置医生
  /** 代理商(以及关联的配药中心)生效的单元药库 */
  allPharmacyList: IMallPharmacyDetail[];
  isAgent: boolean;
  cbSearch(query: IDoctorSearch): void;
  query?: IDoctorSearch;
  /** 代理商是否有归属医生的权限 */
  hasAssignAuth: boolean;
  cbSelectedRow?(selectedRowKeys: string[] | number[]): void;
}

interface IDoctorSearchFormState {
  /** 省份列表 */
  provinceList: IAreaInfo[];
  /** 城市列表 */
  cityList: IAreaInfo[];
  /** 区县列表 */
  districtList: IAreaInfo[];
  sales: ISales[];
  timeout: number | undefined;
}

class DoctorSearchForm extends React.Component<IDoctorSearchFormProps, IDoctorSearchFormState> {
  constructor(props: IDoctorSearchFormProps) {
    super(props);
    this.state = {
      provinceList: [],
      cityList: [],
      districtList: [],
      // 销售数组
      sales: [],
      timeout: undefined,
    };
  }

  async componentDidMount() {
    await this.initArea();
    this.cacheSearchParam();
    // this.getSales();
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  /**
   * 将缓存的查询条件 赋值到 查询控件中
   */
  private cacheSearchParam() {
    if (!!this.props.query) {
      const { isDomestic, query } = this.props;
      const {
        fullname,
        mobile,
        email,
        start_valid_time,
        end_valid_time,
        certification_level,
        medicine_group,
        is_active,
        online_ordering,
        province,
        city,
        area,
      } = query;

      let cityList: IAreaInfo[] = [];
      let districtList: IAreaInfo[] = [];
      if (!!province) {
        const selectedProvince = LocationService.areaMap.get(Number(province)) as IAreaInfo;
        cityList = LocationService.getLinkageAreaByParentId(selectedProvince.value);
      }
      if (!!city) {
        const selectedCity = LocationService.areaMap.get(Number(city)) as IAreaInfo;
        districtList = LocationService.getLinkageAreaByParentId(selectedCity.value);
      }
      this.setState({ cityList, districtList });
      let validityDate: moment.Moment[] = [];
      const startTime = !!start_valid_time ? moment(start_valid_time) : undefined;
      const endTime = !!end_valid_time ? moment(end_valid_time) : undefined;
      if (!!startTime && !!endTime) {
        validityDate = [startTime, endTime];
      }
      this.props.form.setFieldsValue({
        name: fullname,
        [isDomestic ? 'phone' : 'email']: isDomestic ? mobile : email,
        validityDate,
        certification_level,
        medicine_group,
        is_active,
        online_ordering,
        province,
        city,
        area,
      });
    }
  }
  /**
   * 初始化地区服务
   */
  private async initArea() {
    try {
      await LocationService.initArea();
      const provinceList: IAreaInfo[] = LocationService.getLinkageProvince();
      this.setState({
        provinceList,
      });
    } catch (error) {
      console.error(error);
    }
  }

  handleSearch = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.doSearch();
  };

  doSearch = (offset: number = 0, limit: number = 10) => {
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        try {
          let startDate: string | undefined;
          let endDate: string | undefined;
          if (!!values.validityDate && values.validityDate.length > 1) {
            startDate = values.validityDate[0].format('YYYY-MM-DD');
            endDate = values.validityDate[1].format('YYYY-MM-DD');
          }
          let query: IDoctorSearch = {
            limit,
            offset,
            check: true,
            mobile: values.phone,
            fullname: values.name,
            start_valid_time: startDate,
            end_valid_time: endDate,
            certification_level: values.certification_level,
            is_need_configure: this.props.isNeedSetting,
            medicine_group: values.medicine_group,
            online_ordering: values.online_ordering,
            is_active: values.is_active,
            province: values.province,
            city: values.city,
            area: values.area,
            salesperson_uuid: values.salesperson_uuid,
            email: values.email,
          };

          if (query.salesperson_uuid === '-1') {
            query = {...query, salesperson__isnull: true};
            delete query.salesperson_uuid;
          }
          await this.props.cbSearch(query);
        } catch (error) {
          if (!!error.response) {
            message.error(JSON.stringify(error.response.data));
          } else {
            message.error(I18N.get('err_search_failure'));
          }
        }
      }
    });
  };

  handleReset = () => {
    this.props.form.resetFields();
    if (!!this.props.cbSelectedRow) {
      this.props.cbSelectedRow([]);
    }
  };

  getCertificationLevelOption = (): JSX.Element[] => {
    const options: JSX.Element[] = [];
    for (const option of CertificationLevel) {
      if (option.isShow) {
        options.push(
          <Select.Option key={option.key} value={option.value}>
            {I18N.get(option.key)}
          </Select.Option>
        );
      }
    }
    return options;
  };

  /**
   * 单元药库数据变化时触发
   *
   */
  pharmacyChange = (pharmacyUUID: string | null | undefined) => {
    this.props.form.setFieldsValue({ medicine_group: pharmacyUUID });
  };

  /**
   * 省份变化
   */
  provinceChange = (value: string) => {
    let selectedProvince: IAreaInfo | undefined;
    let cityList: IAreaInfo[] = [];
    if (!!value) {
      selectedProvince = LocationService.areaMap.get(Number(value)) as IAreaInfo;
      cityList = LocationService.getLinkageAreaByParentId(selectedProvince.value);
    }
    this.props.form.setFieldsValue({ city: undefined, area: undefined });
    this.setState({
      cityList,
      districtList: [],
    });
  };

  /**
   * 城市变化
   */
  cityChange = (value: string) => {
    let selectedCity: IAreaInfo | undefined;
    let districtList: IAreaInfo[] = [];
    if (!!value) {
      selectedCity = LocationService.areaMap.get(Number(value)) as IAreaInfo;
      districtList = LocationService.getLinkageAreaByParentId(selectedCity.value);
    }
    this.props.form.setFieldsValue({ area: undefined });
    this.setState({
      districtList,
    });
  };

  render() {
    const { isDomestic, allPharmacyList } = this.props;
    const { provinceList, cityList, districtList } = this.state;

    const { getFieldDecorator } = this.props.form;

    return (
      <Form className="mdd-search-form" onSubmit={this.handleSearch}>
        <Row type="flex" align="middle">
          <div className="auto-flex-item">
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item label={I18N.get('common_name')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`name`)(<Input placeholder={I18N.get('common_name')} />)}
                </Form.Item>
              </Col>
              {isDomestic && (
                <Col span={6}>
                  <Form.Item label={I18N.get('common_mobile')} style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`phone`)(<Input placeholder={I18N.get('common_mobile')} />)}
                  </Form.Item>
                </Col>
              )}
              {!isDomestic && (
                <Col span={6}>
                  <Form.Item label="Email" style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`email`)(<Input placeholder="email" />)}
                  </Form.Item>
                </Col>
              )}
              <Col span={6}>
                <Form.Item label={I18N.get('doc_validity_date')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`validityDate`, {
                    rules: [{ type: 'array', message: `${I18N.get('common_choose')}${I18N.get('common_datetime')}` }],
                  })(<RangePicker />)}
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={I18N.get('doc_type')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`certification_level`)(
                    <Select allowClear={true} placeholder={I18N.get('common_choose')}>
                      {this.getCertificationLevelOption()}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
            {isDomestic && (
              <Row gutter={24} style={{ marginTop: 10 }}>
                <Col span={6}>
                  <Form.Item label={I18N.get('order_online')} style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`online_ordering`)(
                      <Select allowClear={true} placeholder={`${I18N.get('common_choose')}`}>
                        <Select.Option key={'true'} value={'true'}>
                          {I18N.get('common_yes')}
                        </Select.Option>
                        <Select.Option key={'false'} value={'false'}>
                          {I18N.get('common_no')}
                        </Select.Option>
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label={I18N.get('unit_pharmacy')} style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`medicine_group`)(
                      <SupplierPharmacySelect pharmacyList={allPharmacyList} showNone={true} onChange={this.pharmacyChange} />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <div className="flex-row">
                    <div className="ant-col ant-form-item-label">
                      <label>{I18N.get('doc_area')}</label>
                    </div>
                    <Form.Item style={{ marginBottom: 0, marginRight: 10, maxWidth: 120 }} className="auto-flex-item">
                      {getFieldDecorator(`province`)(
                        <Select dropdownMatchSelectWidth={false} allowClear={true} onChange={this.provinceChange} placeholder={`${I18N.get('common_choose')}`}>
                          {provinceList.map((province) => (
                            <Select.Option key={String(province.value)} value={province.value}>
                              {province.text}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, marginRight: 10, maxWidth: 120 }} className="auto-flex-item">
                      {getFieldDecorator(`city`)(
                        <Select dropdownMatchSelectWidth={false} allowClear={true} onChange={this.cityChange} placeholder={I18N.get('common_choose')}>
                          {cityList.map((city) => (
                            <Select.Option key={String(city.value)} value={city.value}>
                              {city.text}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, maxWidth: 120 }} className="auto-flex-item">
                      {getFieldDecorator(`area`)(
                        <Select dropdownMatchSelectWidth={false} allowClear={true} placeholder={I18N.get('common_choose')}>
                          {districtList.map((district) => (
                            <Select.Option key={String(district.value)} value={district.value}>
                              {district.text}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </div>
                </Col>
                <Col span={6}>
                  <Form.Item label={I18N.get('doc_enabled')} style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`is_active`)(
                      <Select allowClear={true} placeholder={`${I18N.get('common_choose')}`}>
                        <Select.Option key={'true'} value={'true'}>
                          {I18N.get('common_yes')}
                        </Select.Option>
                        <Select.Option key={'false'} value={'false'}>
                          {I18N.get('common_no')}
                        </Select.Option>
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            )}
            {this.props.isAgent && this.props.hasAssignAuth && (
              <Row gutter={24} style={{ marginTop: 10 }}>
                <Col span={6}>
                  <Form.Item label={I18N.get('doc_sales')} style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`salesperson_uuid`)(
                        <SalesSearchBox />
                    )}
                    {/* (<Input placeholder={I18N.get('doc_sales')}/>) */}
                  </Form.Item>
                </Col>
              </Row>
            )}
          </div>
        </Row>
        <Row type="flex" align="middle">
          {/*  搜索， 清空按钮  */}
          <div className="auto-flex-item" style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <Button type="primary" htmlType="submit">
              {I18N.get('common_search')}
            </Button>
            <Button style={{ marginLeft: 20 }} onClick={this.handleReset}>
              {I18N.get('common_clear')}
            </Button>
          </div>
        </Row>
      </Form>
    );
  }
}

const DoctorSearch = Form.create<IDoctorSearchFormProps>()(DoctorSearchForm);
export default DoctorSearch;



// WEBPACK FOOTER //
// ./src/components/views/Doctor/DoctorSearch.tsx